import { ScrollView, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { Task } from "../../../types/types";
import { useAppSelector } from "../../../redux/store";
import { SafeAreaView } from "react-native-safe-area-context";
import { Field, StatusOption } from "../../../config/companies/config";
import { HorizontalBoardCard } from "./HorizontalBoardCard";
import { getBoardStatusColor } from "../../../utils/boardStatusColor";

interface HorizontalBoardProps {
  sections: { data: Task[]; title: string }[];
  fields: Field[];
  navigateToDetails: (card: Task) => void;
  onStatusChange?: (cardId: string, newStatus: string) => void;
  onMoveWithinColumn?: (card: Task, direction: "up" | "down") => void;
  onSelectionChange?: (selectedCards: Task[]) => void;
  statusOptions: StatusOption[];
  statusColors?: Record<string, string>;
}

export const HorizontalBoard = ({
  sections,
  fields,
  navigateToDetails,
  onStatusChange,
  onMoveWithinColumn,
  statusOptions = [],
  statusColors,
}: HorizontalBoardProps) => {
  const { t } = useTranslation();
  const userRole = useAppSelector((state) => state.auth.user.role);
  const permittedStatusesToEdit = statusOptions
    .filter((status) => status.editableBy.includes(userRole))
    .map((s) => s.value);

  const isStatusEditable = (statusValue: string) => {
    return permittedStatusesToEdit.includes(statusValue);
  };

  const handleStatusChange = (card: Task, direction: "left" | "right") => {
    if (!onStatusChange) return;

    const currentIndex = sections.findIndex((section) =>
      section.data.some((item) => item.id === card.id),
    );

    if (currentIndex === -1) return;

    const newIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < sections.length) {
      onStatusChange(card.id, sections[newIndex].title);
    }
  };

  const handleCardPress = (card: Task) => {
    navigateToDetails(card);
  };

  return (
    <View style={styles.board}>
      <ScrollView
        horizontal
        contentContainerStyle={{ flexDirection: "row" }}
        snapToInterval={300}
        snapToAlignment="start"
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
      >
        {sections?.map((column) => (
          <ScrollView
            key={column.title}
            style={styles.column}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.columnHeaderRow}>
              <Text style={styles.columnTitle}>
                {t(column.title)}{" "}
                <Text
                  style={[
                    styles.count,
                    { color: getBoardStatusColor(column.title, statusColors) },
                  ]}
                >
                  ({column.data.length})
                </Text>
              </Text>
            </View>
            {column.data.map((card) => (
              <HorizontalBoardCard
                key={card.id}
                card={card}
                sections={sections}
                handleCardPress={handleCardPress}
                fields={fields}
                handleStatusChange={handleStatusChange}
                handleInColumnMove={onMoveWithinColumn}
                isStatusEditable={isStatusEditable}
                statusColors={statusColors}
              />
            ))}
            <SafeAreaView />
          </ScrollView>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  board: {
    flex: 1,
  },
  column: {
    width: 300,
    padding: 15,
    borderRadius: 10,
  },
  columnHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: 12,
  },
  columnTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: "#0f172a",
  },
  count: {
    fontWeight: "700",
  },
  list: {
    // height: screenHeight - 150,
  },
  listContent: {
    paddingBottom: 20,
  },
  card: {
    marginBottom: 15,
  },
  cardActions: {
    justifyContent: "space-between",
    paddingHorizontal: 8,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: "#2196F3",
  },
  activeMenuItem: {
    backgroundColor: "#E3F2FD",
  },
  exportContainer: {
    alignSelf: "center",
    padding: 8,
    marginTop: 40,
  },
  exportTitle: {
    fontSize: 16,
    marginBottom: 8,
    fontWeight: "bold",
  },
  exportCount: {
    fontSize: 14,
    marginBottom: 10,
  },
  attachmentIcon: {
    marginBottom: 4,
    color: "#1976D2",
  },
});
